import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError, bezugsSchluessel } from "@kundeninfo/api-client";
import type { ApiClient, Artikel, Position, PositionFelder, Positionsbezug, PositionsStand,
              Steuersatz } from "@kundeninfo/api-client";
import { formatDezimal, formatMenge } from "../format.js";
import { renderMarkdown } from "../markdown.js";
import { MarkdownHinweis } from "./MarkdownHinweis.js";
import { Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import "./PositionsEditor.css";
import { useBestaetigung } from "@kundeninfo/ui";

/**
 * Der Positions-Editor eines Vorgangs — EINE Komponente für Angebot UND Auftrag
 * (Paket 3b-1b, Aufgabe 4).
 *
 * Sie sitzt am Angebot (KundeAngebote.tsx), unverändert am Auftrag (KundeAuftraege.tsx) und
 * seit Paket 3b-2 ebenso am RECHNUNGS-ENTWURF (KundeRechnungen.tsx — an einer
 * festgeschriebenen Rechnung wird sie gar nicht erst eingehängt): Der einzige Unterschied ist
 * der Bezug (`bezug`), und den reicht sie an den Klienten durch. Ein zweiter, nachgebauter
 * Editor wäre die sicherste Art, dass dieselbe Zeile an zwei Bildschirmen Verschiedenes
 * bedeutet.
 *
 * Seit Migration 029 sitzt sie ausserdem an der ANGEBOTSVORLAGE
 * (`views/verwaltung/angebotsvorlagen.tsx` über `components/VorlageDetail.tsx`) — der
 * vierte Einsatzort, und der erste ohne Kunden.
 *
 * **Der Server rechnet, die Maske zeigt — AUSNAHMSLOS.** Zeilennettos, Zwischensummen und
 * Steuersummen kommen fertig aus der Antwort (`zeilennettos`, `zwischensummen`,
 * `steuersummen`) — dieselbe Rechnung, die später hinter dem PDF steht. Hier wird KEINE Zahl
 * gebildet, auch keine Zeilenzahl: Das Zeilennetto stand eine Runde lang als Nachbau von
 * `domain/itemRules` in dieser Datei (weil `web` `domain` nicht importieren darf) und ist mit
 * Ruling P3B1B-I ersatzlos entfallen. Es kaufte nichts ein — gerechnet wurde ohnehin auf dem
 * SERVERSTAND, nicht auf dem Entwurf, die Spalte sprang also erst nach dem Speichern — und
 * kostete eine zweite Rundungswahrheit.
 *
 * **Jede Antwort trägt die frische `version`.** Positionen hängen an der optimistischen
 * Sperre des VORGANGS: Jeder Schreibweg schickt die Version und bekommt die neue zurück. Wer
 * eine davon nicht übernimmt, bekommt beim nächsten Klick 409 — und ab da bei jedem weiteren.
 * Deshalb läuft jede Antwort durch `uebernehmen()`, und jede 409 lädt neu statt zu meckern.
 *
 * Eigener CSS-Block `pe__` in einer EIGENEN Datei (PositionsEditor.css), mit keiner Ansicht
 * geteilt — Kollisions-Lehre 01.09.
 */

/** Nur die Klientenmethoden, die der Editor wirklich braucht — so lässt er sich im Test mit
 *  einer Handvoll vi.fn zeichnen (Muster der bestehenden Reiter-Tests). */
export type PositionsEditorClient = Pick<ApiClient,
  "listItems" | "createItem" | "updateItem" | "deleteItem" | "reorderItems" | "resetItem"
  | "searchProducts" | "listActiveTaxRates">;

export type PositionsEditorProps = {
  client: PositionsEditorClient;
  /**
   * WORAN die Positionen hängen: ein Vorgang eines Kunden (`offer`/`order`/`invoice`) oder
   * eine ANGEBOTSVORLAGE (`template`, Migration 029).
   *
   * EINE Eigenschaft und nicht drei: Eine Vorlage hat keinen Kunden, und ein `kundeId`, das
   * dort leer bliebe, wäre die Sorte Feld, die beim nächsten Lesen für ein Versehen gehalten
   * wird. Der Typ kommt aus dem Klienten (`Positionsbezug`) und ist hier nicht nachgebaut —
   * als die Rechnung dazukam, brauchte diese Datei deshalb keine Zeile Änderung, und als
   * die Vorlage dazukam, ebenso wenig: Der Editor reicht den Bezug nur durch, die Familie
   * entscheidet der Server.
   */
  bezug: Positionsbezug;
  /** Das Unternehmen des Vorgangs — die Artikelsuche zeigt NUR dessen Artikelstamm. Ein
   *  firmenfremder Artikel wäre serverseitig ohnehin ein Feldfehler. */
  companyId: string;
  /** Die zuletzt bekannte Version des VORGANGS. Ändert der Elternteil den Kopf (und damit
   *  die Version), reicht er sie hier herein — sonst liefe der nächste Positionsschritt in
   *  eine 409. */
  version: number;
  /** Wird nach JEDER Antwort gerufen: die frische Version und der ganze Stand (aus dem der
   *  Elternteil `netAmount` und „hat Positionen?" liest). */
  onVersionChange: (version: number, stand: PositionsStand) => void;
  /**
   * Die umgebende Maske sperrt (ME-Etappe 3, Aufgabe 4, Fixrunde 1): solange ihr
   * Formularzustand nicht „bereit“ ist — ein Speichern läuft oder ein fremder Stand wartet
   * auf „Neu laden“. Dann schreibt der Editor nichts (sonst liefe er in seine eigene 409)
   * und seine Knöpfe sind zu; die Zelleneingaben sperrt das Formular selbst (readOnly).
   */
  gesperrt?: boolean | undefined;
};

/** Die Gliederungsebenen — dieselbe Grenze wie `ITEM_LEVEL_MAX` in der Domäne (0..3). Als
 *  AUSWAHL, nicht als Zahlenfeld: Eine getippte 7 wäre ein Feldfehler statt einer Ebene. */
const EBENEN = [0, 1, 2, 3];

// ---------------------------------------------------------------------------
// Einleitungs- und Ausleitungstext mit Platzhalter-Vorschau
// ---------------------------------------------------------------------------

/** Ein Token ist `#{` gefolgt von einem beliebigen Namen ohne `}`, dann `}` — derselbe
 *  Ausdruck wie `PLATZHALTER_RE` in domain/masterdataRules. */
const PLATZHALTER_RE = /#\{([^}]*)\}/g;

/**
 * Die LOKALE Mini-Ersetzung für die Vorschau: bekannte Namen werden durch ihren Wert
 * ersetzt, alle übrigen Tokens bleiben UNVERÄNDERT stehen.
 *
 * Bewusst nicht `platzhalterAufloesen` aus der Domäne — `web` darf `domain` nicht
 * importieren (check-boundaries), und ein Nachbau der Namensliste wäre eine zweite Wahrheit:
 * Die Namen kommen deshalb aus `GET /api/platzhalter`, die Werte aus dem, was der Reiter
 * ohnehin geladen hat. Was der Reiter nicht kennt (z. B. eine Anschrift), bleibt als Token
 * sichtbar — besser eine erkennbare Lücke als ein stillschweigend leerer Satz.
 *
 * GENAU EIN Durchlauf über die Tokens des Ausgangstextes: Ein eingesetzter Wert, der selbst
 * ein Token enthält, wird nicht erneut aufgelöst (keine Rekursion, keine Endlosersetzung) —
 * dieselbe Zusage wie in der Domäne. `String.replace` mit globalem Ausdruck leistet das von
 * sich aus; die Ersetzungen werden nicht noch einmal abgesucht.
 *
 * Verbindlich ist diese Vorschau nicht: Fürs PDF löst die geteilte Render-Engine auf.
 */
export function ersetzePlatzhalter(text: string, werte: Record<string, string>): string {
  return text.replace(PLATZHALTER_RE, (treffer, name: string) =>
    Object.prototype.hasOwnProperty.call(werte, name) ? werte[name]! : treffer);
}

/**
 * Die Laufzeit-Platzhalter für die BILDSCHIRM-Vorschau der Freitextfelder (Migration 031:
 * vier Dauern, jede als Zahl PLUS Einheit — vorher zwei nackte Monatszahlen).
 *
 * Sie muss dieselben Worte zeigen wie das PDF — sonst sieht der Bearbeiter „24 Monate" und
 * der Kunde „2 Jahre". Die verbindliche Formatierung steht in `domain`
 * (`dauerText`/`laufzeitPlatzhalter`, von der Engine benutzt); `packages/web` darf `domain`
 * nicht importieren (check-boundaries), also steht sie hier ein zweites Mal — klein genug
 * dafür. Es gibt KEINEN Test, der diese Fassung wörtlich gegen `dauerText` hält (der
 * bräuchte den verbotenen Import); `kundeAngebote.test.tsx` prüft nur INDIREKT, indem es
 * die erwartete Wortform („2 Jahre") in der gerenderten Vorschau erwartet — eine
 * abweichende zweite Fassung fiele dort auf, ein stillschweigendes Auseinanderlaufen der
 * REGEL selbst (neue Einheit, neue Pluralform) nicht zwingend (Fund M5 der
 * Abschlussprüfung).
 *
 * Die Argumente sind ROHTEXTE aus dem Formular-Entwurf, keine vorkonvertierten Zahlen: Ein
 * geleertes Feld erzeugt so von selbst keinen Eintrag, ohne dass jeder Aufrufer erst
 * `ganzzahlOderNull` bemühen müsste — ein fehlender oder halber Wert lässt den Platzhalter
 * in der Vorschau stehen, statt als Leerstelle zu verschwinden. Genau daran sieht der
 * Bearbeiter, dass die Laufzeit noch zu ergänzen ist.
 *
 * Es sind ZWÖLF Namen: je Grösse der ganze Satz, `….wert` und `….einheit` — die beiden
 * Teile reisen einzeln mit, falls ein künftiger Beleg sie getrennt braucht.
 */
export function laufzeitVorschauWerte(felder: {
  termValue: string; termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string; minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string; renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string; noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
}): Record<string, string> {
  const werte: Record<string, string> = {};

  /** „Monat"/„Monate"/„Jahr"/„Jahre" — passend zur Zahl (wie `einheitWort` in domain). */
  function einheit(zahl: number, art: "MONTHS" | "YEARS"): string {
    if (art === "MONTHS") return zahl === 1 ? "Monat" : "Monate";
    return zahl === 1 ? "Jahr" : "Jahre";
  }

  function dreifach(name: string, roh: string, art: "MONTHS" | "YEARS",
                    ganz?: (text: string) => string): void {
    const kern = roh.trim();
    if (kern === "") return;
    const zahl = Number(kern);
    if (!Number.isInteger(zahl) || zahl < 0) return;
    const wortform = einheit(zahl, art);
    const text = `${String(zahl)} ${wortform}`;
    werte[name] = ganz === undefined ? text : ganz(text);
    werte[`${name}.wert`] = String(zahl);
    werte[`${name}.einheit`] = wortform;
  }

  dreifach("laufzeit.vertragslaufzeit", felder.termValue, felder.termUnit);
  dreifach("laufzeit.mindestlaufzeit", felder.minimumTermValue, felder.minimumTermUnit);
  dreifach("laufzeit.verlaengerung", felder.renewalPeriodValue, felder.renewalPeriodUnit);
  dreifach("laufzeit.kuendigungsfrist", felder.noticePeriodValue, felder.noticePeriodUnit,
    (text) => `${text} ${felder.noticeReference === "TERM_END"
      ? "zum Ende der Laufzeit" : "zum Ende des Kalenderjahres"}`);
  return werte;
}

export type PlatzhalterTextfeldProps = {
  id: string;
  label: string;
  /** Der ROHTEXT mit seinen `#{…}`-Tokens — genau das, was gespeichert wird. */
  wert: string;
  onChange: (wert: string) => void;
  /** Die bekannten Namen aus `GET /api/platzhalter` (ohne die #{}-Klammern) — als Legende
   *  unter dem Feld, damit niemand raten muss, was es überhaupt gibt. */
  variablen: string[];
  /** Die im Reiter geladenen Werte, nach Platzhaltername. Was fehlt, bleibt stehen. */
  werte: Record<string, string>;
  rows?: number;
};

/**
 * Ein Textfeld für Einleitungs- bzw. Ausleitungstext: ROHTEXT oben, leichte Vorschau
 * darunter.
 *
 * Steht bewusst in DIESER Datei und nicht in der Ansicht: Es gehört zum selben
 * wiederverwendeten Editor (Angebot heute, Auftrag mit Aufgabe 5), trägt denselben
 * CSS-Block `pe__` und dieselbe Stildatei. In der Ansicht nachgebaut, gäbe es die Vorschau
 * zweimal — und die zweite wäre eines Tages die andere.
 *
 * **Die Vorschau rendert MARKDOWN, das Eingabefeld bleibt Rohtext** (Entscheid 05.09.2026).
 * Belegtexte werden geschrieben, um gesetzt zu werden — `**fett**` und eine Aufzählung
 * gehören im Beleg als solche gesetzt, und die Vorschau ist der Ort, an dem der Bearbeiter
 * das schon beim Tippen sieht. Gerendert wird mit dem VORHANDENEN `renderMarkdown`
 * (markdown.tsx, `kn-md`-Präfix, sanitisierend) — demselben, der Notizen und
 * Artikelbeschreibungen setzt; ein zweiter Renderer wäre ein zweiter Satz Sicherheitsregeln.
 * Insbesondere bleibt dessen Regel unangetastet, dass ein EXTERNES Bild als Link erscheint
 * und nicht als `<img>` (kein Ausspähkanal über die IP des Betrachters).
 *
 * Die Platzhalter werden VOR dem Rendern ersetzt: Der Bearbeiter soll den Satz sehen, der
 * entsteht — nicht `#{firma.name}` mitten im gesetzten Text. Verbindlich ist auch das
 * nicht; fürs PDF löst die geteilte Render-Engine auf (siehe `ersetzePlatzhalter`).
 */
export function PlatzhalterTextfeld(p: PlatzhalterTextfeldProps): ReactElement {
  const vorschau = ersetzePlatzhalter(p.wert, p.werte);
  return (
    <div className="pe__textblock">
      <label className="pe__label" htmlFor={p.id}>{p.label}</label>
      <textarea id={p.id} className="pe__textarea" rows={p.rows ?? 3}
        value={p.wert} onChange={(e) => p.onChange(e.target.value)} />
      {/* Bei leerem Text KEINE Vorschau: sonst steht der Seitenbalken allein als
          verwaiste senkrechte Linie über der Legende (Befund Dirk 05.09.).

          Ein `<div>` und kein `<p>` mehr: renderMarkdown liefert Blöcke (Absätze, Listen,
          Tabellen), und die dürfen in einem Absatz gar nicht stehen. `role="group"` trägt
          dabei die Beschriftung — an einem blossen `<div>` würde ein aria-label von den
          meisten Vorlesern gar nicht angesagt. */}
      {vorschau.trim() !== "" && (
        <div className="pe__vorschau" role="group" aria-label={`${p.label} — Vorschau`}>
          {renderMarkdown(vorschau)}
        </div>
      )}
      {/* Zwei Fussnoten als Paar: erst die Sprache, in der hier geschrieben wird, dann
          die Wörter, die sie kennt. Der Markdown-Satz kommt aus dem gemeinsamen Baustein
          (MarkdownHinweis.tsx) — dieses eine Feld bedient Angebot, Auftrag UND Rechnung,
          also spart es allein schon drei Abschriften. */}
      <MarkdownHinweis />
      {p.variablen.length > 0 && (
        <p className="pe__legende">
          Platzhalter: {p.variablen.map((v) => `#{${v}}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

const ART_LABEL: Record<Position["kind"], string> = {
  ITEM: "Position",
  SUBTOTAL: "Zwischensumme",
  TEXT: "Textzeile",
};

// ---------------------------------------------------------------------------
// Zahlen — Anzeige und Eingabe
// ---------------------------------------------------------------------------

/*
 * Die Zahlenformatierer stehen seit der Fixrunde 1 zu Aufgabe 7 in `format.ts`
 * (`formatDezimal`, `formatMenge`): Dieselben zwei `Intl`-Einstellungen standen wortgleich
 * hier, im Rechnungsdetail und in der Vorlagensicht — drei Gelegenheiten, dass derselbe
 * Betrag an drei Bildschirmen verschieden aussieht.
 */

/** Dasselbe für einen Betrag, den die Antwort GAR NICHT führt: Eine Textzeile hat kein
 *  Zeilennetto, und „0,00 €" wäre dort eine Behauptung. Dann bleibt die Zelle leer. */
function formatBetragOderLeer(wert: string | undefined): string {
  return wert === undefined ? "" : formatDezimal(wert);
}

/** Deutsche Kommazahl mitverstehen: „2.500,50" wie „2500.50" (Muster aus KundeAngebote). */
function zahlAus(wert: string): number {
  const normalisiert = wert.trim().replace(/\./g, "").replace(",", ".");
  if (normalisiert === "") return 0;
  const zahl = Number(normalisiert);
  return Number.isFinite(zahl) ? zahl : Number.NaN;
}

// ---------------------------------------------------------------------------
// Der Bearbeitungsstand einer Zeile
// ---------------------------------------------------------------------------

/** Die Werte EINER Zeile im Formular. Alle Zahlen als Zeichenkette, damit ein halb
 *  getippter Wert („1,") das Feld nicht zurücksetzt.
 *
 * `customerOrderLine` und `billingProbability` (Paket Teilrechnungen) gehören zur
 * POSITION, nicht zur Zwischensumme oder Textzeile — genau wie Menge und Einzelpreis
 * stehen sie hier als TEXT, die Umsetzung in Zahl macht `felderAus`. */
type ZeilenEntwurf = {
  name: string;
  description: string;
  unit: string;
  quantity: string;
  netUnitPrice: string;
  customerOrderLine: string;
  billingProbability: string;
};

function entwurfAus(item: Position): ZeilenEntwurf {
  return {
    name: item.name,
    description: item.description ?? "",
    unit: item.unit ?? "",
    quantity: formatMenge(item.quantity),
    netUnitPrice: formatDezimal(item.netUnitPrice),
    customerOrderLine: item.customerOrderLine ?? "",
    billingProbability: String(item.billingProbability),
  };
}

function entwuerfeAus(items: Position[]): Record<string, ZeilenEntwurf> {
  const alle: Record<string, ZeilenEntwurf> = {};
  for (const item of items) alle[item.id] = entwurfAus(item);
  return alle;
}

/** Die zehn Schreibfelder einer Zeile aus dem bestehenden Satz plus den Formularwerten. Die
 *  Stelle in der Folge (`sortIndex`) gehört NICHT dazu — sie ändert nur `reorderItems`. */
function felderAus(item: Position, entwurf: ZeilenEntwurf): PositionFelder {
  const istPosition = item.kind === "ITEM";
  return {
    kind: item.kind,
    level: item.level,
    productId: item.productId,
    name: entwurf.name.trim(),
    // Getrimmt wie der Name — und zwar in BEIDEN Zweigen. Wurde nur über den Leerraum
    // entschieden („leer oder nicht") und der ungetrimmte Wert geschickt, löste ein einzelnes
    // eingetipptes Leerzeichen hinter dem Text einen Schreibweg aus, den der Server danach
    // wegnormalisierte (Fund G6).
    description: entwurf.description.trim() === "" ? null : entwurf.description.trim(),
    // SUBTOTAL und TEXT tragen weder Einheit noch Zahlen noch Steuersatz — die Domäne weist
    // alles andere als Feldfehler ab, und die Maske bietet die Felder dort gar nicht erst an.
    unit: !istPosition || entwurf.unit.trim() === "" ? null : entwurf.unit.trim(),
    quantity: istPosition ? zahlAus(entwurf.quantity) : 0,
    netUnitPrice: istPosition ? zahlAus(entwurf.netUnitPrice) : 0,
    taxRateId: istPosition ? item.taxRateId : null,
    pageBreakBefore: item.pageBreakBefore,
    // Beide gehören zur POSITION, nicht zur Zwischensumme oder Textzeile. An der Rechnung
    // gibt es für beide Felder keine Eingabe (siehe `zeile()`) — der Entwurf trägt dort
    // unverändert den geladenen Wert weiter, und genau der geht hier mit, statt eines
    // `undefined`: Ein Antwortschema mit `additionalProperties: false` verlangt jedes
    // Feld auf BEIDEN Seiten (Global Constraint).
    customerOrderLine: !istPosition || entwurf.customerOrderLine.trim() === ""
      ? null : entwurf.customerOrderLine.trim(),
    billingProbability: istPosition
      ? Math.round(zahlAus(entwurf.billingProbability)) : 100,
  };
}

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext. Feldfehler
 *  (422) werden angehängt — sie sagen genau, welche Spalte der Zeile nicht durchgeht. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (!(fehler instanceof ApiError)) return standard;
  const felder = (fehler.fields ?? []).map((f) => f.message).join(" ");
  const kern = fehler.message.trim() === "" ? standard : fehler.message;
  return felder === "" ? kern : `${kern} ${felder}`;
}

const KONFLIKT_HINWEIS =
  "Dieser Vorgang wurde zwischenzeitlich geändert. Die Positionen sind neu geladen — "
  + "bitte den Schritt wiederholen.";

/** Die Rückmeldung, wenn an einer gesperrten Maske doch jemand eine Zeile ändern will
 *  (Aufgabe 8, Re-Review A4): Vorher schluckte der Riegel den Versuch still. */
const GESPERRT_HINWEIS =
  "Die Positionen sind gerade gesperrt — die Maske speichert, oder der Vorgang wurde "
  + "inzwischen geändert (oben „Neu laden“).";

/** Welche Art von Zeile ein Einfügepunkt gerade anbietet (null = zu). */
type Einfuegewahl = { stelle: number; artikelSuche: boolean };

export function PositionsEditor(p: PositionsEditorProps): ReactElement {
  const { client, bezug, companyId } = p;

  // Die Abhängigkeit ist der INHALT des Bezugs, nicht das Objekt: Das Literal im JSX ist bei
  // jedem Zeichnen ein neues, und ein Effekt an ihm lädt nach jedem Zeichnen neu. Gebildet
  // wird er beim TYP (`bezugsSchluessel` in @kundeninfo/api-client): Die Zahlplan-Tafel
  // braucht denselben Schlüssel, und eine zweite Abschrift derselben Regel liefe beim
  // nächsten Bezugsfall auseinander.
  const schluessel = bezugsSchluessel(bezug);

  const [stand, setStand] = useState<PositionsStand | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, ZeilenEntwurf>>({});
  const [saetze, setSaetze] = useState<Steuersatz[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [schreibt, setBeschaeftigt] = useState(false);
  /** Ein eigener Schreibweg läuft — ODER die umgebende Maske sperrt (`gesperrt`). */
  const beschaeftigt = schreibt || p.gesperrt === true;
  const [offeneTexte, setOffeneTexte] = useState<Record<string, boolean>>({});
  const [einfuegen, setEinfuegen] = useState<Einfuegewahl | null>(null);
  const [suchtext, setSuchtext] = useState("");
  const [treffer, setTreffer] = useState<Artikel[] | null>(null);
  const [gezogen, setGezogen] = useState<string | null>(null);

  /** Die Version, mit der der NÄCHSTE Schreibweg anfragt. Als Ref, nicht als Zustand: Sie
   *  wird mitten in einem Handler gelesen, unmittelbar nachdem ein anderer Schritt sie
   *  gesetzt hat — ein Zustandswert wäre dort noch der alte des Renderdurchlaufs. */
  const version = useRef(p.version);
  /** Der Rückruf in einer Ref, damit `laden` stabil bleibt: Läge er in den Abhängigkeiten
   *  des Lade-Effekts, zöge jede neue Funktionsidentität des Elternteils ein erneutes Laden
   *  nach sich — und weil das Laden wieder meldet, liefe es endlos. */
  const meldeVersion = useRef(p.onVersionChange);
  useEffect(() => { meldeVersion.current = p.onVersionChange; }, [p.onVersionChange]);
  useEffect(() => { version.current = p.version; }, [p.version]);

  /** Der aktuelle Bezug, ausserhalb der Abhängigkeitsliste von `laden` erreichbar (Muster
   *  `vorlageIdRef` in `views/verwaltung/angebotsvorlagen.tsx`): Das Objektliteral, das ein
   *  Aufrufer im JSX übergibt, ist bei jedem Zeichnen ein neues — läge es selbst in der
   *  Abhängigkeitsliste, bekäme `laden` bei jedem Zeichnen eine neue Identität, und der
   *  Lade-Effekt darunter liefe endlos nach. */
  const bezugRef = useRef(bezug);
  bezugRef.current = bezug;

  const uebernehmen = useCallback((neu: PositionsStand): void => {
    setStand(neu);
    setEntwuerfe(entwuerfeAus(neu.items));
    version.current = neu.version;
    meldeVersion.current(neu.version, neu);
  }, []);

  const laden = useCallback(async (): Promise<void> => {
    try {
      uebernehmen(await client.listItems(bezugRef.current));
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Positionen konnten nicht geladen werden."));
    }
  }, [client, schluessel, uebernehmen]);

  useEffect(() => { void laden(); }, [laden]);

  // Die Steuersätze für die Auswahlspalte. Scheitert das, bleibt der Editor bedienbar — nur
  // die Auswahl ist dann leer (die bestehende Zuordnung einer Zeile bleibt trotzdem stehen,
  // siehe satzOptionen unten).
  useEffect(() => {
    let ab = false;
    client.listActiveTaxRates()
      .then(({ items }) => { if (!ab) setSaetze(items); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  /**
   * Der gemeinsame Rahmen JEDES Schreibwegs: Version hinein, frischen Stand heraus.
   *
   * 409 heisst nicht „Fehler", sondern „jemand anders war schneller": neu laden und sagen,
   * dass der Schritt zu wiederholen ist. Alles andere (422 mit Feldfehlern, 404, 500) bleibt
   * als Meldung stehen, ohne den Stand anzufassen.
   */
  async function schreibe(
    lauf: (version: number) => Promise<PositionsStand>, standard: string,
  ): Promise<void> {
    // Die Maske sperrt — kein Schreibweg, aber eine Rückmeldung statt Stille.
    if (p.gesperrt === true) { setHinweis(GESPERRT_HINWEIS); return; }
    setBeschaeftigt(true);
    setHinweis(null);
    try {
      uebernehmen(await lauf(version.current));
      setFehler(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setHinweis(KONFLIKT_HINWEIS);
        await laden();
      } else {
        setFehler(alsMeldung(e, standard));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  const items = stand?.items ?? [];

  /**
   * Die Sperre der beiden Auswahlen (Steuersatz, Ebene) bei `gesperrt`: Ein `<select>` kennt
   * kein readOnly. `disabled` nähme ihm den Fokus (gegen die Formular-Regel „gesperrt, der
   * Fokus bleibt"); deshalb `aria-disabled` und die Bedienung selbst abgefangen — die Liste
   * klappt nicht auf, Pfeiltasten wählen nichts, Tab geht weiter. Was trotzdem durchkommt
   * (Touch-Auswahl am Telefon), fängt der Riegel in `schreibe` mit Rückmeldung ab.
   */
  const auswahlSperre = p.gesperrt === true ? {
    "aria-disabled": true as const,
    onMouseDown: (e: { preventDefault(): void }) => {
      e.preventDefault();
      setHinweis(GESPERRT_HINWEIS);
    },
    onKeyDown: (e: { key: string; preventDefault(): void }) => {
      if (e.key === "Tab" || e.key === "Escape") return;
      e.preventDefault();
      setHinweis(GESPERRT_HINWEIS);
    },
  } : {};

  /**
   * Die Optionen der Steuersatz-Spalte: die AKTIVEN Sätze — und, falls die Zeile auf eine
   * stillgelegte oder abgelaufene Zeitscheibe zeigt, zusätzlich genau diese.
   *
   * Ohne den zweiten Teil verlöre ein altes Angebot beim ersten Speichern still seinen
   * Steuersatz: Das Auswahlfeld stünde auf einem Wert, den es nicht anbietet, und der Browser
   * meldete den ersten Eintrag zurück. Die Auflösung zum Belegdatum macht weiterhin der
   * Server; hier geht es nur darum, den Bestand nicht zu überschreiben.
   */
  function satzOptionen(item: Position): { id: string; text: string }[] {
    const optionen = saetze.map((s) => ({ id: s.id, text: `${s.name} (${s.ratePercent} %)` }));
    if (item.taxRateId !== null && !optionen.some((o) => o.id === item.taxRateId)) {
      optionen.unshift({ id: item.taxRateId, text: "bisheriger Satz (nicht mehr gepflegt)" });
    }
    return optionen;
  }

  function setzeEntwurf(id: string, feld: keyof ZeilenEntwurf, wert: string): void {
    setEntwuerfe((alle) => {
      const vorher = alle[id];
      if (vorher === undefined) return alle;
      return { ...alle, [id]: { ...vorher, [feld]: wert } };
    });
  }

  /** Speichert eine Zeile, wenn sich an ihr etwas geändert hat — beim Verlassen des Feldes.
   *  Ein Speicherweg je Tastenanschlag wäre ein Schreibsturm auf den Vorgang, und jeder davon
   *  drehte die Version weiter. */
  async function zeileSichern(item: Position): Promise<void> {
    const entwurf = entwuerfe[item.id];
    if (entwurf === undefined) return;
    const felder = felderAus(item, entwurf);
    const unveraendert =
      felder.name === item.name && felder.description === item.description
      && felder.unit === item.unit && felder.quantity === item.quantity
      && felder.netUnitPrice === item.netUnitPrice
      && felder.customerOrderLine === item.customerOrderLine
      && felder.billingProbability === item.billingProbability;
    if (unveraendert) return;
    await schreibe(
      (v) => client.updateItem(bezug, item.id, v, felder),
      "Die Zeile konnte nicht gespeichert werden.");
  }

  /** Sofort-Änderungen (Auswahlfeld, Schalter) — sie haben keinen Zwischenzustand, den ein
   *  Verlassen abschliessen müsste. */
  async function zeileAendern(item: Position, teil: Partial<PositionFelder>): Promise<void> {
    const entwurf = entwuerfe[item.id] ?? entwurfAus(item);
    await schreibe(
      (v) => client.updateItem(
        bezug, item.id, v, { ...felderAus(item, entwurf), ...teil }),
      "Die Zeile konnte nicht geändert werden.");
  }

  async function zeileLoeschen(item: Position): Promise<void> {
    await schreibe(
      (v) => client.deleteItem(bezug, item.id, v),
      "Die Zeile konnte nicht gelöscht werden.");
  }

  const { bestaetigen, bestaetigung } = useBestaetigung();
  async function zeileZuruecksetzen(item: Position): Promise<void> {
    // Rückfrage, weil es die eingetippten Werte der Zeile verwirft — die MENGE bleibt.
    if (!(await bestaetigen({
      titel: "Zeile zurücksetzen?",
      text: `„${item.name}" auf die Werte aus dem Artikelstamm zurücksetzen? `
        + "Name, Beschreibung, Einheit, Preis und Steuersatz werden neu geholt; "
        + "die Menge bleibt.",
      bestaetigenText: "Zurücksetzen",
    }))) return;
    await schreibe(
      (v) => client.resetItem(bezug, item.id, v),
      "Die Zeile konnte nicht zurückgesetzt werden.");
  }

  // -------------------------------------------------------------------------
  // Einfügen
  // -------------------------------------------------------------------------

  /** Die Ebene, auf der eine neue Zeile beginnt: die der Zeile darüber. Wer in einen
   *  eingerückten Block einfügt, meint fast immer denselben Block. */
  function ebeneAn(stelle: number): number {
    return stelle === 0 ? 0 : (items[stelle - 1]?.level ?? 0);
  }

  function leereFelder(kind: Position["kind"], stelle: number): PositionFelder {
    const istPosition = kind === "ITEM";
    return {
      kind,
      level: ebeneAn(stelle),
      productId: null,
      // SUBTOTAL darf namenlos sein, ITEM und TEXT nicht — ein leerer Name käme als
      // Feldfehler zurück, bevor die Zeile überhaupt auf dem Bildschirm steht.
      name: kind === "ITEM" ? "Neue Position" : kind === "TEXT" ? "Text" : "Zwischensumme",
      description: null,
      unit: istPosition ? "Stück" : null,
      quantity: istPosition ? 1 : 0,
      netUnitPrice: 0,
      taxRateId: istPosition ? (saetze[0]?.id ?? null) : null,
      pageBreakBefore: false,
      // Eine neue Zeile trägt noch keine Bestellposition und die volle
      // Abrechnungswahrscheinlichkeit (Vorgabe 100) — beide sind an Angebot und Auftrag
      // sofort bearbeitbar.
      customerOrderLine: null,
      billingProbability: 100,
    };
  }

  async function einfuegenAn(stelle: number, kind: Position["kind"]): Promise<void> {
    setEinfuegen(null);
    await schreibe(
      (v) => client.createItem(
        bezug, v, leereFelder(kind, stelle), stelle),
      "Die Zeile konnte nicht eingefügt werden.");
  }

  /** Der gewählte Artikel wird als SCHNAPPSCHUSS kopiert: Name, Beschreibung, Einheit, Preis
   *  und Steuersatz stehen danach an der Zeile. `productId` bleibt als HERKUNFT stehen — sie
   *  trägt später das Zurücksetzen; eine spätere Preisänderung im Stamm ändert das Angebot
   *  aber nicht von selbst. */
  async function artikelEinfuegen(stelle: number, artikel: Artikel): Promise<void> {
    setEinfuegen(null);
    setTreffer(null);
    setSuchtext("");
    await schreibe(
      (v) => client.createItem(bezug, v, {
        kind: "ITEM",
        level: ebeneAn(stelle),
        productId: artikel.id,
        name: artikel.name,
        description: artikel.description,
        unit: artikel.unit,
        quantity: 1,
        netUnitPrice: artikel.netUnitPrice,
        taxRateId: artikel.taxRateId,
        pageBreakBefore: false,
        customerOrderLine: null,
        billingProbability: 100,
      }, stelle),
      "Der Artikel konnte nicht eingefügt werden.");
  }

  async function artikelSuchen(): Promise<void> {
    try {
      const { items: gefunden } = await client.searchProducts({
        companyId, suche: suchtext, limit: 20,
      });
      setTreffer(gefunden);
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Artikelsuche ist fehlgeschlagen."));
    }
  }

  // -------------------------------------------------------------------------
  // Umsortieren — zwei gleichwertige Wege
  // -------------------------------------------------------------------------

  /**
   * Der EINE Umsortier-Weg, den beide Bedienarten benutzen: Ziehen und die Pfeilknöpfe.
   *
   * Geschickt wird immer die KOMPLETTE Id-Folge, nicht ein Verschiebebefehl — der Server
   * nimmt genau die vorhandenen Kennungen, jede einmal; eine Teilliste wäre mehrdeutig.
   */
  async function verschiebe(von: number, nach: number): Promise<void> {
    const ids = items.map((i) => i.id);
    if (von < 0 || nach < 0 || von >= ids.length || nach >= ids.length || von === nach) return;
    const geordnet = [...ids];
    const [bewegt] = geordnet.splice(von, 1);
    geordnet.splice(nach, 0, bewegt!);
    await schreibe(
      (v) => client.reorderItems(bezug, v, geordnet),
      "Die Reihenfolge konnte nicht gespeichert werden.");
  }

  /** Die gezogene Zeile steht im Zustand, nicht im `dataTransfer`: Letzteres ist in jsdom
   *  nicht durchgängig vorhanden, und die Kennung wird ohnehin nur innerhalb dieser Tabelle
   *  gebraucht. `setData` wird trotzdem versucht — manche Browser starten sonst gar kein
   *  Ziehen. */
  function ziehenStart(item: Position, uebertrag: DataTransfer | null): void {
    setGezogen(item.id);
    uebertrag?.setData?.("text/plain", item.id);
  }

  async function ablegenAuf(ziel: Position): Promise<void> {
    const quelleId = gezogen;
    setGezogen(null);
    if (quelleId === null || quelleId === ziel.id) return;
    const ids = items.map((i) => i.id);
    await verschiebe(ids.indexOf(quelleId), ids.indexOf(ziel.id));
  }

  // -------------------------------------------------------------------------
  // Zeichnen
  // -------------------------------------------------------------------------

  function einfuegepunkt(stelle: number): ReactElement {
    const offen = einfuegen?.stelle === stelle;
    return (
      <tr className="pe__einfuegen" key={`einfuegen-${stelle}`}>
        <td className="pe__einfuegen-zelle" colSpan={7}>
          <button type="button" className="pe__plus" disabled={beschaeftigt}
            aria-label={`An Stelle ${stelle + 1} einfügen`}
            onClick={() => {
              // Treffer und Suchtext gehören zu DEM Einfügepunkt, an dem gesucht wurde —
              // stehen sie beim nächsten offen, fügt ein unbedachter Klick den Artikel an
              // der falschen Stelle ein.
              setTreffer(null);
              setSuchtext("");
              setEinfuegen(offen ? null : { stelle, artikelSuche: false });
            }}>
            +
          </button>
          {offen && (
            <span className="pe__einfuegen-wahl">
              <button type="button" className="pe__klein" disabled={beschaeftigt}
                onClick={() => setEinfuegen({ stelle, artikelSuche: true })}>
                Artikel suchen
              </button>
              <button type="button" className="pe__klein" disabled={beschaeftigt}
                onClick={() => { void einfuegenAn(stelle, "ITEM"); }}>
                Freie Position
              </button>
              <button type="button" className="pe__klein" disabled={beschaeftigt}
                onClick={() => { void einfuegenAn(stelle, "SUBTOTAL"); }}>
                Zwischensumme
              </button>
              <button type="button" className="pe__klein" disabled={beschaeftigt}
                onClick={() => { void einfuegenAn(stelle, "TEXT"); }}>
                Textzeile
              </button>
            </span>
          )}
          {offen && einfuegen?.artikelSuche === true && (
            <span className="pe__suche">
              {/* Eingabetaste sucht — ein Suchfeld, das nur über den Knopf daneben geht,
                  ist im Alltag ein Stolperstein. Kein <form>: Der Editor steht INNERHALB
                  des Angebotsformulars, und ein verschachteltes Formular ist ungültiges
                  HTML (der Browser würde es auflösen). */}
              <input className="pe__eingabe" type="search" aria-label="Artikel suchen"
                value={suchtext} onChange={(e) => setSuchtext(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void artikelSuchen(); }
                }} />
              <button type="button" className="pe__klein"
                onClick={() => { void artikelSuchen(); }}>
                Suchen
              </button>
              {treffer !== null && treffer.length === 0 && (
                <span className="pe__leer">Kein Artikel gefunden.</span>
              )}
              {treffer !== null && treffer.length > 0 && (
                <ul className="pe__treffer">
                  {treffer.map((a) => (
                    <li key={a.id}>
                      <button type="button" className="pe__klein"
                        onClick={() => { void artikelEinfuegen(stelle, a); }}>
                        {a.productNumber} · {a.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </span>
          )}
        </td>
      </tr>
    );
  }

  function zeile(item: Position, index: number): ReactElement {
    const entwurf = entwuerfe[item.id] ?? entwurfAus(item);
    const istPosition = item.kind === "ITEM";
    const nummer = index + 1;
    const textOffen = offeneTexte[item.id] === true || entwurf.description !== "";
    return (
      // Ziehen startet NUR am Griff (nicht an der ganzen Zeile): Liegt `draggable` an einem
      // Vorfahren, startet ein Mausziehen im Eingabefeld das Ziehen der Zeile statt einer
      // Textmarkierung — man könnte in keinem Feld mehr Text markieren. Ablegen bleibt an
      // der ganzen Zeile, damit die Trefferfläche gross ist.
      <tr key={item.id}
        className={`pe__zeile pe__zeile--${item.kind.toLowerCase()}`
          + (gezogen === item.id ? " pe__zeile--zieht" : "")}
        data-kind={item.kind} data-ebene={item.level}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); void ablegenAuf(item); }}>

        <td className="pe__zelle pe__zelle--griff">
          <span className="pe__griff" role="button" tabIndex={-1}
            aria-label={`Zeile ${nummer} verschieben`}
            draggable
            onDragStart={(e) => ziehenStart(item, e.dataTransfer)}
            onDragEnd={() => setGezogen(null)}>⠿</span>
          <span className="pe__art">{ART_LABEL[item.kind]}</span>
          {item.pageBreakBefore && (
            <span className="pe__umbruch" title="Seitenumbruch vor dieser Zeile">⤓</span>
          )}
          {/* Die Abrechnungswahrscheinlichkeit ist LESEND an jedem Einsatzort sichtbar —
              bearbeitbar nur an Angebot und Auftrag (siehe unten in `pe__zeilen-aktionen`).
              Bei 100 % (der Regelfall) bleibt die Pille weg: Nur die Ausnahme ist eine
              Meldung wert. */}
          {item.billingProbability < 100 && (
            <span className="pe__pille">Budget, {item.billingProbability} %</span>
          )}
        </td>

        <td className="pe__zelle pe__zelle--name"
          style={{ paddingLeft: `${8 + item.level * 20}px` }}>
          <input className="pe__eingabe" type="text"
            aria-label={`Bezeichnung Zeile ${nummer}`}
            value={entwurf.name}
            onChange={(e) => setzeEntwurf(item.id, "name", e.target.value)}
            onBlur={() => { void zeileSichern(item); }} />
          <button type="button" className="pe__klein"
            aria-expanded={textOffen}
            onClick={() => setOffeneTexte((o) => ({ ...o, [item.id]: !textOffen }))}>
            Beschreibung
          </button>
          {textOffen && (
            <textarea className="pe__textarea" rows={2}
              aria-label={`Beschreibung Zeile ${nummer}`}
              value={entwurf.description}
              onChange={(e) => setzeEntwurf(item.id, "description", e.target.value)}
              onBlur={() => { void zeileSichern(item); }} />
          )}
        </td>

        <td className="pe__zelle pe__zelle--zahl" data-ausrichtung="rechts">
          {istPosition && (
            <input className="pe__eingabe pe__eingabe--zahl" type="text" inputMode="decimal"
              aria-label={`Menge Zeile ${nummer}`}
              value={entwurf.quantity}
              onChange={(e) => setzeEntwurf(item.id, "quantity", e.target.value)}
              onBlur={() => { void zeileSichern(item); }} />
          )}
        </td>

        <td className="pe__zelle">
          {istPosition && (
            <input className="pe__eingabe pe__eingabe--kurz" type="text"
              aria-label={`Einheit Zeile ${nummer}`}
              value={entwurf.unit}
              onChange={(e) => setzeEntwurf(item.id, "unit", e.target.value)}
              onBlur={() => { void zeileSichern(item); }} />
          )}
        </td>

        <td className="pe__zelle pe__zelle--zahl" data-ausrichtung="rechts">
          {istPosition && (
            <input className="pe__eingabe pe__eingabe--zahl" type="text" inputMode="decimal"
              aria-label={`Einzelpreis Zeile ${nummer}`}
              value={entwurf.netUnitPrice}
              onChange={(e) => setzeEntwurf(item.id, "netUnitPrice", e.target.value)}
              onBlur={() => { void zeileSichern(item); }} />
          )}
        </td>

        <td className="pe__zelle">
          {istPosition && (
            <select className="pe__select" aria-label={`Steuersatz Zeile ${nummer}`}
              {...auswahlSperre}
              value={item.taxRateId ?? ""}
              onChange={(e) => { void zeileAendern(item,
                { taxRateId: e.target.value === "" ? null : e.target.value }); }}>
              <option value="">— ohne Steuer —</option>
              {satzOptionen(item).map((o) => (
                <option key={o.id} value={o.id}>{o.text}</option>
              ))}
            </select>
          )}
        </td>

        <td className="pe__zelle pe__zelle--betrag" data-ausrichtung="rechts">
          {/* Beide Beträge sind BERECHNET und kommen aus der Antwort — an der Zeile
              gespeichert (oder hier nachgerechnet) wären sie eine zweite Wahrheit. Fehlt
              der Eintrag, ist die Zeile eine, die keinen Betrag trägt (TEXT): dann steht
              dort nichts, nicht „0,00". */}
          <span className="ki-num">
            {formatBetragOderLeer(item.kind === "SUBTOTAL"
              ? stand?.zwischensummen[item.id]
              : stand?.zeilennettos[item.id])}
          </span>
          <span className="pe__zeilen-aktionen">
            {/* Der zweite, GLEICHWERTIGE Weg zum Umsortieren. Natives HTML5-Ziehen hat auf
                iOS-Safari keine Touch-Entsprechung — und das System ist eine PWA für iPhone
                und iPad. Ohne diese beiden Knöpfe liesse sich dort gar nichts verschieben,
                mit der Tastatur ebenso wenig. Beide Wege laufen durch `verschiebe`. */}
            <button type="button" className="pe__klein"
              disabled={beschaeftigt || index === 0}
              aria-label={`Zeile ${nummer} nach oben`}
              onClick={() => { void verschiebe(index, index - 1); }}>
              ▲
            </button>
            <button type="button" className="pe__klein"
              disabled={beschaeftigt || index === items.length - 1}
              aria-label={`Zeile ${nummer} nach unten`}
              onClick={() => { void verschiebe(index, index + 1); }}>
              ▼
            </button>
            <select className="pe__select pe__select--ebene" aria-label={`Ebene Zeile ${nummer}`}
              {...auswahlSperre}
              value={item.level}
              onChange={(e) => { void zeileAendern(item, { level: Number(e.target.value) }); }}>
              {EBENEN.map((e) => <option key={e} value={e}>Ebene {e}</option>)}
            </select>
            <label className="pe__schalter">
              <input type="checkbox" checked={item.pageBreakBefore}
                aria-disabled={p.gesperrt === true ? true : undefined}
                aria-label={`Seitenumbruch vor Zeile ${nummer}`}
                onChange={(e) => { void zeileAendern(item,
                  { pageBreakBefore: e.target.checked }); }} />
              Umbruch
            </label>
            {/* Bestellposition des Kunden und Abrechnungswahrscheinlichkeit (Paket
                Teilrechnungen) — bearbeitbar NUR an Angebot und Auftrag (Ruling T1): An
                einer Rechnung ist die Zuordnung bereits geschehen (beim Rechnungsvorschlag)
                und ausdrücklich nicht mehr änderbar (YAGNI, Spec). */}
            {istPosition && bezug.art !== "invoice" && (
              <>
                <input className="pe__eingabe pe__eingabe--kurz" type="text"
                  aria-label={`Bestellposition des Kunden Zeile ${nummer}`}
                  placeholder="Bestellposition des Kunden"
                  value={entwurf.customerOrderLine}
                  onChange={(e) =>
                    setzeEntwurf(item.id, "customerOrderLine", e.target.value)}
                  onBlur={() => { void zeileSichern(item); }} />
                <input className="pe__eingabe pe__eingabe--kurz" type="text"
                  inputMode="numeric"
                  aria-label={`Abrechnungswahrscheinlichkeit Zeile ${nummer}`}
                  title="Abrechnungswahrscheinlichkeit in Prozent (Vorgabe 100)"
                  value={entwurf.billingProbability}
                  onChange={(e) =>
                    setzeEntwurf(item.id, "billingProbability", e.target.value)}
                  onBlur={() => { void zeileSichern(item); }} />
              </>
            )}
            {/* An der Rechnung ist die Bestellposition NUR lesend sichtbar (Ruling): Sie
                sagt, WOHER die Zeile kommt, aber sie steht dort nicht mehr zur Wahl. */}
            {istPosition && bezug.art === "invoice" && item.customerOrderLine !== null && (
              <span className="pe__bestellposition-lese">{item.customerOrderLine}</span>
            )}
            {item.productId !== null && (
              <button type="button" className="pe__klein" disabled={beschaeftigt}
                aria-label={`Zeile ${nummer} auf den Artikel zurücksetzen`}
                onClick={() => { void zeileZuruecksetzen(item); }}>
                Zurücksetzen
              </button>
            )}
            <button type="button" className="pe__klein" disabled={beschaeftigt}
              aria-label={`Zeile ${nummer} löschen`}
              onClick={() => { void zeileLoeschen(item); }}>
              Löschen
            </button>
          </span>
        </td>
      </tr>
    );
  }

  return (
    <section className="pe" aria-label="Positionen">
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}

      {stand === null && fehler === null && <p className="pe__leer">Lädt …</p>}

      {stand !== null && (
        <>
          {/* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Kopfzeile, Linien und das
              waagerechte Rollen bringt das Element; Zahlen rechtsbündig über
              data-ausrichtung. Der EDITOR bleibt unverändert: Eingaben in den Zellen,
              Ziehen am Griff, Pfeile, Einfügepunkte zwischen den Zeilen. */}
          <Tabelle>
          <table className="pe__tabelle">
            <thead>
              <tr>
                <th scope="col">Art</th>
                <th scope="col">Bezeichnung</th>
                <th scope="col" data-ausrichtung="rechts">Menge</th>
                <th scope="col">Einheit</th>
                <th scope="col" data-ausrichtung="rechts">Einzelpreis</th>
                <th scope="col">Steuersatz</th>
                <th scope="col" data-ausrichtung="rechts">Netto</th>
              </tr>
            </thead>
            <tbody>
              {einfuegepunkt(0)}
              {items.map((item, index) => (
                // Zeile UND ihr Einfügepunkt darunter gehören zusammen — ein Fragment mit
                // Schlüssel hält beide beim Umsortieren beieinander.
                <Fragment key={item.id}>
                  {zeile(item, index)}
                  {einfuegepunkt(index + 1)}
                </Fragment>
              ))}
            </tbody>
          </table>
          </Tabelle>

          {items.length === 0 && (
            <p className="pe__leer">Noch keine Positionen — über „+" die erste einfügen.</p>
          )}

          <dl className="pe__summen">
            {stand.steuersummen.jeSatz.map((f) => (
              <div className="pe__summe" key={f.taxRateId ?? "ohne"}>
                <dt>{f.name} ({f.ratePercent} %) auf {formatDezimal(f.netto)} €</dt>
                <dd className="ki-num">{formatDezimal(f.steuer)} €</dd>
              </div>
            ))}
            <div className="pe__summe">
              <dt>Gesamt netto</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtNetto)} €</dd>
            </div>
            <div className="pe__summe">
              <dt>Umsatzsteuer</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtSteuer)} €</dd>
            </div>
            <div className="pe__summe pe__summe--brutto">
              <dt>Gesamt brutto</dt>
              <dd className="ki-num">{formatDezimal(stand.steuersummen.gesamtBrutto)} €</dd>
            </div>
          </dl>
        </>
      )}
      {bestaetigung}
    </section>
  );
}
